import { inject } from "@angular/core";
import { Observable, catchError, map, of } from "rxjs";
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot, UrlTree } from "@angular/router";
import { CookieService } from "ngx-cookie-service";
import { v4 as uuidv4 } from 'uuid';

import { UserService } from "../services/user.service";
import { COOKIE, CONSTANTS, SPLUNK } from "../../modules/login/util/constants";
import { PingParameter } from "../../shared/constants/constants";
import { LoginService } from "../../modules/login/services/login.service";
import { LoginResponse } from "../../modules/login/models/login.response.model";
import { LoggerService } from "../../shared/services/logger.service";

export const inboundSsoGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
): Observable<boolean | UrlTree> | boolean | UrlTree => {

  const userService = inject(UserService);
  const loginService = inject(LoginService);
  const cookieService = inject(CookieService);
  const loggerService = inject(LoggerService);
  const router = inject(Router);

  const REF_ID = route.queryParams[PingParameter.REF];

  if (REF_ID) {
    const request = { referenceIdentifier: REF_ID };
    return loginService.authenticateUser(request)
      .pipe(
        map((response: LoginResponse) => {
          if (response?.responseHeader?.transactionNotification?.status === CONSTANTS.SUCCESS) {
            // Set Session ID
            userService.setSessionID = uuidv4();
            cookieService.set(COOKIE.DP_ADM_SID, btoa(userService.sessionId),
              { domain: window.location.hostname, path: '/', sameSite: 'Strict', secure: true });

            // Update cookies and redirect to Home
            const userName = response.responseBody.username;
            const jwtToken = response.responseBody.token;
            const userId = response.responseBody.userId;
            const adAccessLevel = response.responseBody.adAccessLevel;

            // Set User Name
            cookieService.set(COOKIE.DP_ADM_USER_NAME, btoa(userName),
              { domain: window.location.hostname, path: '/', sameSite: 'Strict', secure: true });

            // Set JWT Token
            userService.setJwtToken = jwtToken;
            cookieService.set(COOKIE.DP_ADM_TOKEN, btoa(jwtToken),
              { domain: window.location.hostname, path: '/', sameSite: 'Strict', secure: true });

            // Set User Id
            userService.setUserIdentifier = userId;
            cookieService.set(COOKIE.DP_ADM_USER_ID, btoa(userId),
              { domain: window.location.hostname, path: '/', sameSite: 'Strict', secure: true });

            // Set Access Level
            if (adAccessLevel) {
              cookieService.set(COOKIE.DP_ADM_AL, btoa(adAccessLevel),
                { domain: window.location.hostname, path: '/', sameSite: 'Strict', secure: true });
            }

            // Splunk Update
            loggerService.logEvent(
              SPLUNK.SALES_FORCE_SSO_SUCCESS_EVENT_CD,
              SPLUNK.SALES_FORCE_SSO_SUCCESS_EVENT_DESC,
              SPLUNK.SALES_FORCE_SSO_SUCCESS_ID,
              '',
              {
                adminUserId: userId,
                adminUserName: userName
              },
              CONSTANTS.SUCCESS
            );
            const targetURL = route.queryParams['targetURL']
            if (targetURL) {
              const tree: UrlTree = router.parseUrl(targetURL)
              return tree;
            } else {
              const benefitLookupUrl = cookieService.get(COOKIE.BENEFIT_LOOKUP_URL);
              cookieService.delete(COOKIE.BENEFIT_LOOKUP_URL);
              // SSO Redirection 
              if ((benefitLookupUrl !== null && benefitLookupUrl !== "") && benefitLookupUrl !== undefined) {
                router.navigateByUrl(benefitLookupUrl);
              } else {
                return router.createUrlTree([CONSTANTS.HOME_URL]);
              }
              return true;
            }
          } else {
            // Check if there are any error messages
            if (response?.responseHeader?.transactionNotification?.remarks?.messages?.length) {
              // Get the error message
              const errorMessage = response.responseHeader.transactionNotification.remarks.messages[0];
              // Check error code and set appropriate error message
              if (errorMessage.code === CONSTANTS.UNAUTHORIZED_CODE || errorMessage.code === CONSTANTS.USER_NOT_FOUND_CODE) {
                return navigateToLogin(userService, router, loggerService, CONSTANTS.INVALID_CREDENTIALS);
              } else if (errorMessage.code === CONSTANTS.ACCOUNT_INACTIVE_CODE) {
                return navigateToLogin(userService, router, loggerService, CONSTANTS.INVALID_ACCOUNT);

              } else {
                return navigateToLogin(userService, router, loggerService, CONSTANTS.LOGIN_EXECEPTION);
              }
            } else {
              // If no error messages, set LOGIN_EXCEPTION as error message
              return navigateToLogin(userService, router, loggerService, CONSTANTS.LOGIN_EXECEPTION);
            }
          }
        }), catchError(() => {
          return of(navigateToLogin(userService, router, loggerService, CONSTANTS.LOGIN_EXECEPTION));
        })
      );
  } else {
    return navigateToLogin(userService, router, loggerService, CONSTANTS.LOGIN_EXECEPTION);
  }

};


const navigateToLogin = (
  userService: UserService,
  router: Router,
  loggerService: LoggerService,
  errorMessage: string
): boolean | UrlTree => {
  loggerService.logEvent(
    SPLUNK.SALES_FORCE_SSO_FAILURE_EVENT_CD,
    SPLUNK.SALES_FORCE_SSO_FAILURE_EVENT_DESC,
    SPLUNK.SALES_FORCE_SSO_FAILURE_ID,
    '',
    {
      adminUserName: CONSTANTS.SALESFORCE_SSO_USER
    },
    CONSTANTS.FAILURE
  );
  userService.ssoLoginErrorMessage$.next(errorMessage);
  userService.logoutUser();
  return router.createUrlTree(['login']);
}